import React from 'react';
import {
  FieldArrayWithId,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  useFormContext,
} from 'react-hook-form';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { Item } from '../Item/Item';
import { DraggableItem } from '../DraggableItem/DraggableItem';
import { Bucket } from '../Bucket/Bucket';
import { useBucketList } from './useBucketList';
import { FormFieldValues } from '../../AssetBucketing.meta';
import { ManagementStyleToggle } from '../ManagementStyleToggle/ManagementStyleToggle';

interface Props {
  bucketFields: FieldArrayWithId<FormFieldValues, 'buckets'>[];
  updateBucket: UseFieldArrayUpdate<FormFieldValues, 'buckets'>;
  replaceBucket: UseFieldArrayReplace<FormFieldValues, 'buckets'>;
  isDefaultAssetBucketingLayout: boolean;
}

export const BucketList: React.FC<Props> = ({
  bucketFields,
  updateBucket,
  replaceBucket,
  isDefaultAssetBucketingLayout,
}) => {
  const { watch } = useFormContext();

  const enableManagementStylesBucketing = watch('enableManagementStylesBucketing');

  const {
    data: { activeAsset, unassignedClassifications },
    actions: { handleDragStart, handleDragEnd, removeItem },
  } = useBucketList({ updateBucket, replaceBucket });

  return (
    <Box mt="24">
      <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Box display="flex" gridGap="8">
          {bucketFields.map(({ classifications, id }, index) => (
            <Bucket
              key={id}
              droppableId={id}
              bucketIndex={index}
              classifications={classifications}
              removeItem={removeItem}
            />
          ))}
        </Box>

        <Box mt="24" mb="8">
          <Box mb="8" display="flex" justifyContent="space-between" alignItems="center">
            <Text as="h2" fontWeight={600} color="neutral.600" fontSize="h4">
              Unassigned {enableManagementStylesBucketing ? 'Management Styles' : 'Assets'}
            </Text>

            <ManagementStyleToggle isDefaultAssetBucketingLayout={isDefaultAssetBucketingLayout} />
          </Box>

          <Separator />

          <Box
            display="grid"
            as="ul"
            mt="24"
            p={0}
            css={`
              list-style: none;
              grid-template-columns: repeat(3, 1fr);
              gap: ${({ theme }) => theme.space[8]}px;
            `}
          >
            {unassignedClassifications.map(classification => (
              <Box as="li" key={classification.classificationId}>
                <DraggableItem id={classification.classificationId} {...classification} />
              </Box>
            ))}
          </Box>
        </Box>

        <DragOverlay>{activeAsset ? <Item {...activeAsset} isDragging /> : null}</DragOverlay>
      </DndContext>
    </Box>
  );
};
